import { useQuery } from '@tanstack/react-query';
import { When } from '@teamsparta/react';
import { Flex } from '@teamsparta/stack-flex';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import type { GetCommunityPostCommentsResponseDto } from '@/api/community/comment/comment.dto';
import type { GetCommunityPostDetailResponseDto } from '@/api/community/post';
import { LoggingClick } from '@/components/common/LoggingClick';
import { LoggingView } from '@/components/common/LoggingView';
import Only from '@/components/common/Only';
import { Suspense } from '@/components/common/Suspense';
import {
  communityPostDetailQuery,
  usePatchCommunityPostViewIncrease,
} from '@/hooks/react-query/community/post';
import { profileQuery } from '@/hooks/react-query/community/profile';

import {
  BackToFeed,
  CommunityLayout,
  Notices,
  OngoingPollPosts,
  Profile,
} from '../../shared';
import { DeletedPostDetailTemplate, PostDetail } from './components';
import { getPostType } from './logic';
import * as S from './style';

interface Props {
  postDetail: GetCommunityPostDetailResponseDto;
  comments: GetCommunityPostCommentsResponseDto | null;
}

export function PostDetailTemplate({
  postDetail: postDetailFromServer,
  comments,
}: Props) {
  const router = useRouter();
  const postId = router.query.id as string;
  const { data } = useQuery(communityPostDetailQuery({ postId }));
  const postDetail = data ?? postDetailFromServer;

  const { mutate: increaseViewCount } = usePatchCommunityPostViewIncrease();

  useEffect(() => {
    if (!postDetail.isDeleted) {
      increaseViewCount({ postId });
    }
  }, [increaseViewCount, postId, postDetail.isDeleted]);

  const { data: profile, isSuccess } = useQuery(profileQuery);

  return (
    <LoggingView
      logName='hh_community_post_view'
      data={{
        post_id: postDetail._id,
        profile_id: profile?._id,
        type: getPostType(postDetail),
        category: postDetail.category,
      }}
      disabled={!isSuccess}
    >
      <When
        condition={!postDetail.isDeleted}
        fallback={<DeletedPostDetailTemplate />}
      >
        <Only.Desktop>
          <CommunityLayout>
            <Flex.Row gap={60}>
              <S.Container>
                <Suspense clientOnly fallback={<Profile.Skeleton />}>
                  <Profile />
                </Suspense>
                <Suspense clientOnly fallback={<OngoingPollPosts.Skeleton />}>
                  <OngoingPollPosts />
                </Suspense>
                <Suspense clientOnly fallback={<Notices.Skeleton />}>
                  <Notices />
                </Suspense>
              </S.Container>
              <Flex.Column
                gap={{ mobile: 40, desktop: 16 }}
                style={{ flex: 1, maxWidth: 580 }}
              >
                <LoggingClick
                  logName='hh_community_post_click'
                  data={{
                    post_id: postDetail._id,
                    button_text: '목록으로',
                  }}
                >
                  <BackToFeed />
                </LoggingClick>
                <PostDetail postDetail={postDetail} comments={comments} />
              </Flex.Column>
            </Flex.Row>
          </CommunityLayout>
        </Only.Desktop>
        <Only.Mobile>
          <CommunityLayout>
            <Flex.Column gap={40} fullWidth>
              <LoggingClick
                logName='hh_community_post_click'
                data={{
                  post_id: postDetail._id,
                  button_text: '목록으로',
                }}
              >
                <BackToFeed />
              </LoggingClick>
              <PostDetail postDetail={postDetail} comments={comments} />
            </Flex.Column>
          </CommunityLayout>
        </Only.Mobile>
      </When>
    </LoggingView>
  );
}
